import {
  TRIAL_SUMMARY_DAYS, choiceEndsTrial, feature, featureAvailable, featureOn, formatMoney, inTrial, inUpgradeWeek, trialUsageEmpty,
  upgradeDaysLeft, upgradeWeekEnded, type Plan,
} from "@szerviz/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../context";
import type { MySubscription } from "../data";
import { ErrorText } from "./ErrorText";

/**
 * The trial of a new workshop (owner's decision, 2026-11-03): 30 days free, the first 7 days with every Profi
 * feature, then Alap until day 30. The guidance cards: the "try it this week" checklist on the start page,
 * the summary two days before the Profi week ends, and the banner once it has ended. Plan tags show which
 * features are Profi only while the trial runs.
 */

/** Dates without the closing dot of some languages (hu: "2026. 12. 02."), so a sentence can go on after it. */
export const useDate = () => {
  const { i18n } = useTranslation();
  return (iso: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(i18n.language).replace(/\.$/, "") : "–");
};

/** A card closed by this viewer (kept in the browser; it simply shows again when the storage is not there). */
function useClosed(name: string): [boolean, () => void] {
  const { workshop } = useApp();
  const key = `garagevo.trial.${name}.${workshop.id}`;
  const [closed, setClosed] = useState(() => {
    try {
      return globalThis.localStorage?.getItem(key) === "1";
    } catch {
      return false;
    }
  });
  const close = () => {
    setClosed(true);
    try {
      globalThis.localStorage?.setItem(key, "1");
    } catch {
      // the card stays closed until the page is reloaded
    }
  };
  return [closed, close];
}

/**
 * Chooses a plan for the owner. During the trial Profi and Premium start today (the trial ends, billing starts),
 * so it asks first; Alap is only recorded (free until the end of the trial). Returns whether it was chosen.
 */
export function useChoosePlan() {
  const { t } = useTranslation();
  const { store, workshop, reloadWorkshop, reloadSubscription } = useApp();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const choose = async (plan: Plan, sub: MySubscription): Promise<boolean> => {
    if (inTrial(sub, sub.today) && choiceEndsTrial(plan) && !globalThis.confirm?.(t("subscription.confirmNow", { plan: t(`plan.${plan}.label`) }))) return false;
    setError(null);
    setBusy(true);
    try {
      await store.setSubscription(workshop.id, { plan });
      await reloadWorkshop();
      await reloadSubscription?.();
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { choose, error, busy };
}

/** A small "Profi" tag next to a feature that Alap does not have, while the trial runs. */
export function PlanTag({ featureKey }: { featureKey: string }) {
  const { t } = useTranslation();
  const { subscription } = useApp();
  const f = feature(featureKey);
  if (!f || f.plan === "basic" || !subscription || !inTrial(subscription, subscription.today)) return null;
  return (
    <span className="plan-tag" title={t("trial.tagTitle", { plan: t(`plan.${f.plan}.label`) })}>
      {t(`plan.${f.plan}.label`)}
    </span>
  );
}

const CHECKLIST = [
  { key: "quotes", page: "workOrders", count: "quoteLinks" },
  { key: "stock", page: "inventory", count: "stockMovements" },
  { key: "reminders", page: "vehicles", count: "reminders" },
] as const;

/**
 * Start page, owner, during the Profi week: three things worth trying, each with a button to its place (it
 * switches the feature on first when it is off). A step ticks itself off once the workshop did it; the card
 * can be closed.
 */
export function TrialChecklist() {
  const { t } = useTranslation();
  const { store, workshop, role, go, reloadWorkshop, subscription: sub } = useApp();
  const [closed, close] = useClosed("checklist");
  const [error, setError] = useState<unknown>(null);
  const date = useDate();
  if (closed || role !== "owner" || !sub || sub.readOnly || !inUpgradeWeek(sub, sub.today)) return null;
  const usage = sub.trialUsage ?? { quoteLinks: 0, stockMovements: 0, reminders: 0 };
  const open = async (key: string, page: string) => {
    setError(null);
    try {
      // the quote link needs the "quotes" feature; the others are one feature each
      if (!featureOn(workshop, key) && featureAvailable(workshop.plan, key, workshop.extraFeatures)) {
        await store.saveFeatures(workshop.id, [...new Set([...workshop.features, key])]);
        await reloadWorkshop();
      }
      go(page);
    } catch (err) {
      setError(err);
    }
  };
  return (
    <section className="trial-card trial-checklist" aria-labelledby="trial-checklist-title">
      <h2 id="trial-checklist-title">{t("trial.checklist.title")}</h2>
      <p className="muted">{t("trial.checklist.intro", { date: date(sub.trialUpgradeUntil) })}</p>
      <ErrorText error={error} />
      <ol className="trial-steps">
        {CHECKLIST.map((c) => {
          const done = usage[c.count] > 0;
          return (
            <li key={c.key} className={done ? "done" : undefined}>
              <span className="trial-tick" aria-hidden="true">{done ? "✓" : ""}</span>
              <span className="trial-step-text">
                {t(`trial.checklist.${c.key}`)}
                {done && <> <span className="badge ok">{t("trial.checklist.done")}</span></>}
              </span>
              <button type="button" className={done ? undefined : "primary"} onClick={() => void open(c.key, c.page)}>
                {t(`trial.checklist.${c.key}Go`)}
              </button>
            </li>
          );
        })}
      </ol>
      <p className="muted small">{t("trial.noNeed")}</p>
      <button type="button" onClick={close}>{t("common.close")}</button>
    </section>
  );
}

/**
 * Two days before the Profi week ends (owner): what the workshop used that Alap lacks, counted on the server
 * from its own data, with "Profi" and "stay on Alap" as equal choices.
 */
export function TrialSummary({ sub }: { sub: MySubscription }) {
  const { t } = useTranslation();
  const [closed, close] = useClosed("summary");
  const { choose, error, busy } = useChoosePlan();
  if (closed) return null;
  const days = upgradeDaysLeft(sub, sub.today);
  const usage = sub.trialUsage ?? { quoteLinks: 0, stockMovements: 0, reminders: 0 };
  const none = trialUsageEmpty(usage);
  return (
    <div className="billing-notice trial-card trial-summary" role="status" aria-labelledby="trial-summary-title">
      <p>
        <strong id="trial-summary-title">{t("trial.summary.title", { days })}</strong>
      </p>
      <p>
        {none
          ? t("trial.summary.none")
          : t("trial.summary.used", { quotes: usage.quoteLinks, stock: usage.stockMovements, reminders: usage.reminders })}
      </p>
      <p className="muted small">{t("trial.noNeed")}</p>
      <ErrorText error={error} />
      <div className="actions">
        <button type="button" className={none ? undefined : "primary"} disabled={busy} onClick={() => void choose("medium", sub)}>
          {t("trial.summary.choosePro")}
        </button>
        <button type="button" className={none ? "primary" : undefined} disabled={busy} onClick={() => void choose("basic", sub)}>
          {t("trial.summary.stayBasic")}
        </button>
        <button type="button" onClick={close}>{t("common.close")}</button>
      </div>
    </div>
  );
}

/** The Profi week has ended (owner, until a plan is chosen): the data stays, Alap and Profi as equal cards. */
export function ProfiWeekEnded({ sub }: { sub: MySubscription }) {
  const { t, i18n } = useTranslation();
  const [closed, close] = useClosed("weekEnded");
  const [picked, setPicked] = useState<Plan | null>(null);
  const { choose, error, busy } = useChoosePlan();
  const date = useDate();
  if (closed) return null;
  const money = (n: number) => formatMoney(n, "HUF", i18n.language);
  const plans: Plan[] = ["basic", sub.trialUpgradePlan && sub.trialUpgradePlan !== "basic" ? sub.trialUpgradePlan : "medium"];
  return (
    <div className="billing-notice trial-card trial-ended" role="status">
      <p>
        <strong>{t("trial.ended.title")}</strong> {t("trial.ended.text", { date: date(sub.trialUntil) })}
      </p>
      <p className="muted small">{t("trial.noNeed")}</p>
      <fieldset className="choice-cards" disabled={busy}>
        <legend>{t("subscription.choosePlan")}</legend>
        {plans.map((plan) => (
          <label key={plan} className={`choice-card${picked === plan ? " selected" : ""}`}>
            <input type="radio" name="trial-ended-plan" checked={picked === plan} onChange={() => setPicked(plan)} aria-label={t(`plan.${plan}.label`)} />
            <span>
              <strong>{t(`plan.${plan}.label`)}</strong>
              <br />
              <span className="muted">
                {plan === "basic"
                  ? t("trial.card.basic", { price: money(sub.pricing.plans.basic), date: date(sub.trialUntil) })
                  : t("trial.card.pro", { price: money(sub.pricing.plans[plan]) })}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
      <ErrorText error={error} />
      <div className="actions">
        <button type="button" className="primary" disabled={!picked || busy} onClick={() => picked && void choose(picked, sub)}>
          {picked ? t("trial.ended.choose", { plan: t(`plan.${picked}.label`) }) : t("subscription.choosePlan")}
        </button>
        <button type="button" onClick={close}>{t("common.close")}</button>
      </div>
    </div>
  );
}

/** Which trial card shows now for the owner (the read only banner and the last 7 days reminder come first). */
export function trialCard(sub: MySubscription): "summary" | "weekEnded" | null {
  if (sub.readOnly || sub.planAfterTrial) return null;
  if (inUpgradeWeek(sub, sub.today) && upgradeDaysLeft(sub, sub.today) <= TRIAL_SUMMARY_DAYS) return "summary";
  if (upgradeWeekEnded(sub, sub.today)) return "weekEnded";
  return null;
}
